import { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import { Checkbox } from "./ui/checkbox";
import { ScrollArea } from "./ui/scroll-area";
import { Copy, Users, Clock, Info } from "lucide-react";
import { format, addDays, addWeeks } from "date-fns";

// Helper to convert time string to minutes
const timeToMinutes = (time) => {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
};

const CopyShiftsDialog = ({
  open,
  onOpenChange,
  copyType, // "day" or "week"
  sourceDate, // For day: the current date, For week: the week start date
  employees,
  shifts, // Current shifts to show count
  businessHours, // Business hours configuration
  onCopy,
}) => {
  const [selectedEmployees, setSelectedEmployees] = useState("all"); // "all" or "selected"
  const [employeeIds, setEmployeeIds] = useState([]);
  const [conflictAction, setConflictAction] = useState("truncate"); // "truncate" or "skip"
  const [loading, setLoading] = useState(false);

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setSelectedEmployees("all");
      setEmployeeIds([]);
      setConflictAction("truncate");
    }
  }, [open]);

  // Calculate source and target dates
  const getDateRange = () => {
    if (copyType === "day") {
      const targetDate = addDays(sourceDate, 1);
      return {
        sourceStart: format(sourceDate, "yyyy-MM-dd"),
        sourceEnd: format(sourceDate, "yyyy-MM-dd"),
        targetStart: format(targetDate, "yyyy-MM-dd"),
        targetEnd: format(targetDate, "yyyy-MM-dd"),
        sourceLabel: format(sourceDate, "EEE, MMM d"),
        targetLabel: format(targetDate, "EEE, MMM d"),
        targetDate: targetDate,
      };
    } else {
      // Week copy
      const sourceEnd = addDays(sourceDate, 6);
      const targetStart = addWeeks(sourceDate, 1);
      const targetEnd = addDays(targetStart, 6);
      return {
        sourceStart: format(sourceDate, "yyyy-MM-dd"),
        sourceEnd: format(sourceEnd, "yyyy-MM-dd"),
        targetStart: format(targetStart, "yyyy-MM-dd"),
        targetEnd: format(targetEnd, "yyyy-MM-dd"),
        sourceLabel: `${format(sourceDate, "MMM d")} - ${format(sourceEnd, "MMM d")}`,
        targetLabel: `${format(targetStart, "MMM d")} - ${format(targetEnd, "MMM d")}`,
        targetDate: targetStart,
      };
    }
  };

  const dateRange = getDateRange();

  // Get business hours for a specific date
  const getBusinessHoursForDate = (date) => {
    const dayName = date.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
    return businessHours?.hours?.find(h => h.day === dayName);
  };

  // Get relevant shifts to copy
  const getRelevantShifts = () => {
    let relevantShifts = shifts.filter(s => {
      const shiftDate = s.date;
      return shiftDate >= dateRange.sourceStart && shiftDate <= dateRange.sourceEnd;
    });
    
    if (selectedEmployees === "selected" && employeeIds.length > 0) {
      relevantShifts = relevantShifts.filter(s => employeeIds.includes(s.employee_id));
    }
    
    return relevantShifts;
  };

  // Check for shifts that exceed target day's business hours
  const conflictAnalysis = useMemo(() => {
    const relevantShifts = getRelevantShifts();
    const conflicts = [];
    
    relevantShifts.forEach(shift => {
      // Calculate target date for this shift
      const shiftSourceDate = new Date(shift.date);
      const dayOffset = Math.floor((shiftSourceDate - new Date(dateRange.sourceStart)) / (1000 * 60 * 60 * 24));
      const targetDate = addDays(dateRange.targetDate, dayOffset);
      
      const targetHours = getBusinessHoursForDate(targetDate);
      
      if (!targetHours || !targetHours.is_open) {
        conflicts.push({
          shift,
          targetDate,
          reason: "closed",
          message: `${format(targetDate, "EEE")} is closed`
        });
        return;
      }
      
      const shiftEndMinutes = timeToMinutes(shift.end_time);
      const targetCloseMinutes = timeToMinutes(targetHours.close_time);
      const shiftStartMinutes = timeToMinutes(shift.start_time);
      const targetOpenMinutes = timeToMinutes(targetHours.open_time);
      
      if (shiftEndMinutes > targetCloseMinutes) {
        conflicts.push({
          shift,
          targetDate,
          reason: "exceeds_close",
          message: `Shift ends at ${shift.end_time} but ${format(targetDate, "EEE")} closes at ${targetHours.close_time}`,
          canTruncate: shiftStartMinutes < targetCloseMinutes
        });
      } else if (shiftStartMinutes < targetOpenMinutes) {
        conflicts.push({
          shift,
          targetDate,
          reason: "before_open",
          message: `Shift starts at ${shift.start_time} but ${format(targetDate, "EEE")} opens at ${targetHours.open_time}`,
          canTruncate: shiftEndMinutes > targetOpenMinutes
        });
      }
    });
    
    return {
      hasConflicts: conflicts.length > 0,
      conflicts,
      truncatable: conflicts.filter(c => c.canTruncate !== false).length,
      mustSkip: conflicts.filter(c => c.canTruncate === false || c.reason === "closed").length
    };
  }, [shifts, dateRange, selectedEmployees, employeeIds, businessHours]);

  const shiftCount = getRelevantShifts().length;

  // Handle employee selection
  const toggleEmployee = (employeeId) => {
    setEmployeeIds(prev => 
      prev.includes(employeeId)
        ? prev.filter(id => id !== employeeId)
        : [...prev, employeeId]
    );
  };

  const selectAllEmployees = () => {
    setEmployeeIds(employees.map(e => e.id));
  };

  const deselectAllEmployees = () => {
    setEmployeeIds([]);
  };

  // Handle copy
  const handleCopy = async () => {
    setLoading(true);
    try {
      await onCopy({
        source_start_date: dateRange.sourceStart,
        source_end_date: dateRange.sourceEnd,
        target_start_date: dateRange.targetStart,
        employee_ids: selectedEmployees === "all" ? null : employeeIds,
        conflict_action: conflictAnalysis.hasConflicts ? conflictAction : null,
      });
      onOpenChange(false);
    } finally {
      setLoading(false);
    }
  };

  const canCopy = shiftCount > 0 && (selectedEmployees === "all" || employeeIds.length > 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Copy className="w-5 h-5" />
            Copy {copyType === "day" ? "Day" : "Week"} Shifts
          </DialogTitle>
          <DialogDescription>
            Copy shifts from {dateRange.sourceLabel} to {dateRange.targetLabel}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Date Summary */}
          <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50 border">
            <div className="text-center">
              <p className="text-xs text-muted-foreground">From</p>
              <p className="font-medium">{dateRange.sourceLabel}</p>
            </div>
            <div className="text-muted-foreground">→</div>
            <div className="text-center">
              <p className="text-xs text-muted-foreground">To</p>
              <p className="font-medium">{dateRange.targetLabel}</p>
            </div>
          </div>

          {/* Business Hours Conflict Warning */}
          {conflictAnalysis.hasConflicts && (
            <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 space-y-3">
              <div className="flex items-start gap-2 text-amber-600 dark:text-amber-400">
                <Clock className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <div className="text-sm">
                  <p className="font-medium">Business hours conflict</p>
                  <p className="text-amber-600/80 dark:text-amber-400/80">
                    {conflictAnalysis.conflicts.length} shift{conflictAnalysis.conflicts.length !== 1 ? "s" : ""} extend beyond target day's business hours
                  </p>
                </div>
              </div>
              
              {/* Show conflict details */}
              <div className="text-xs text-amber-600/70 dark:text-amber-400/70 space-y-1 ml-6">
                {conflictAnalysis.conflicts.slice(0, 3).map((c, i) => (
                  <p key={i}>• {c.message}</p>
                ))}
                {conflictAnalysis.conflicts.length > 3 && (
                  <p>• ...and {conflictAnalysis.conflicts.length - 3} more</p>
                )}
              </div>

              {/* Conflict resolution options */}
              <div className="ml-6">
                <Label className="text-sm text-amber-600 dark:text-amber-400">How to handle?</Label>
                <RadioGroup value={conflictAction} onValueChange={setConflictAction} className="mt-2">
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="truncate" id="conflict-truncate" />
                    <Label htmlFor="conflict-truncate" className="font-normal cursor-pointer text-sm">
                      <span className="font-medium">Truncate</span> - Adjust shift times to fit
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="skip" id="conflict-skip" />
                    <Label htmlFor="conflict-skip" className="font-normal cursor-pointer text-sm">
                      <span className="font-medium">Skip</span> - Don't copy conflicting shifts
                    </Label>
                  </div>
                </RadioGroup>
              </div>
            </div>
          )}

          {/* Employee Selection */}
          <div className="space-y-3">
            <Label className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              Which employees?
            </Label>
            <RadioGroup value={selectedEmployees} onValueChange={setSelectedEmployees}>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="all" id="all-employees" />
                <Label htmlFor="all-employees" className="font-normal cursor-pointer">
                  All employees ({employees.length})
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="selected" id="selected-employees" />
                <Label htmlFor="selected-employees" className="font-normal cursor-pointer">
                  Selected employees only
                </Label>
              </div>
            </RadioGroup>

            {selectedEmployees === "selected" && (
              <div className="ml-6 space-y-2">
                <div className="flex gap-2 text-xs">
                  <button 
                    className="text-primary hover:underline"
                    onClick={selectAllEmployees}
                  >
                    Select all
                  </button>
                  <span className="text-muted-foreground">|</span>
                  <button 
                    className="text-primary hover:underline"
                    onClick={deselectAllEmployees}
                  >
                    Deselect all
                  </button>
                </div>
                <ScrollArea className="h-40 border rounded-lg p-2">
                  <div className="space-y-2">
                    {employees.map(employee => (
                      <div key={employee.id} className="flex items-center gap-2">
                        <Checkbox
                          id={`emp-${employee.id}`}
                          checked={employeeIds.includes(employee.id)}
                          onCheckedChange={() => toggleEmployee(employee.id)}
                        />
                        <label 
                          htmlFor={`emp-${employee.id}`}
                          className="flex items-center gap-2 text-sm cursor-pointer"
                        >
                          {employee.avatar_url ? (
                            <img 
                              src={employee.avatar_url} 
                              alt={employee.name}
                              className="w-6 h-6 rounded-full object-cover"
                            />
                          ) : (
                            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium avatar-${employee.color}`}>
                              {employee.name.charAt(0)}
                            </div>
                          )}
                          {employee.name}
                        </label>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </div>
            )}
          </div>

          {/* Info note about overwrite behavior */}
          <div className="flex items-start gap-2 p-3 rounded-lg bg-muted/30 border text-sm text-muted-foreground">
            <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <p>
              Existing shifts on {dateRange.targetLabel} will be replaced for {selectedEmployees === "all" ? "all employees" : "selected employees only"}. 
              Other employees' shifts won't be affected.
            </p>
          </div>

          {/* Summary */}
          <div className="p-3 rounded-lg border bg-muted/30">
            <p className="text-sm">
              <span className="font-medium">{shiftCount}</span> shift{shiftCount !== 1 ? "s" : ""} will be copied
              {selectedEmployees === "selected" && employeeIds.length > 0 && (
                <span className="text-muted-foreground">
                  {" "}for {employeeIds.length} employee{employeeIds.length !== 1 ? "s" : ""}
                </span>
              )}
              {conflictAnalysis.hasConflicts && conflictAction === "skip" && (
                <span className="text-amber-600 dark:text-amber-400">
                  {" "}({conflictAnalysis.conflicts.length} will be skipped)
                </span>
              )}
              {conflictAnalysis.hasConflicts && conflictAction === "truncate" && (
                <span className="text-amber-600 dark:text-amber-400">
                  {" "}({conflictAnalysis.truncatable} will be adjusted)
                </span>
              )}
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleCopy} 
            disabled={!canCopy || loading}
            data-testid="confirm-copy-btn"
          >
            {loading ? "Copying..." : `Copy ${shiftCount} Shift${shiftCount !== 1 ? "s" : ""}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CopyShiftsDialog;
